import { type FC, useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { type AsyncState } from 'react-use/lib/useAsyncFn';
import styled from 'styled-components';

import { Text, colors } from '@soluto-private/mx-asurion-ui-react';

import { type Suggestion } from '@service-initiation/shared-data-access-google';
import { ClickableText } from '@service-initiation/shared-ui-clickable-text';
import { LocationInput } from '@service-initiation/shared-ui-location-input';
import { TranslationEntries } from '@service-initiation/tech-care-data-access-i18n';
import { type ErrorType } from '@service-initiation/tech-care-data-access-tech-care-services';

import { GeolocationButton } from './GeolocationButton';

const TextFieldWrapper = styled.div<{ validationError: boolean }>`
  padding: ${({ validationError }) => (validationError ? '12px 0 0 0' : '0px')};
`;

const TransWrapper = styled.div`
  margin-bottom: 16px;
`;

const TEXT = TranslationEntries.Repairs.ZipCode;
const ADOTCOM_STORE_LOCATOR_URL = 'https://www.asurion.com/store-locator/';

export type ZipCodeFormProps = {
  evaluateGeolocation: boolean;
  geolocationPositionError: number;
  handleTextChange: (str: string) => Promise<void>;
  inputText: string;
  isLoading: boolean;
  onLocationSelect: (place: Suggestion) => void;
  predictions: Suggestion[] | undefined;
  setInputText?: (str: string) => void;
  showGeolocationPrompt: () => void;
  state: AsyncState<boolean>;
  validationError: ErrorType | undefined;
};

export const ZipCodeForm: FC<ZipCodeFormProps> = (props) => {
  const {
    evaluateGeolocation,
    handleTextChange,
    inputText,
    isLoading,
    onLocationSelect,
    predictions,
    state,
    validationError,
  } = props;
  const { t } = useTranslation();

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    validationError && inputRef.current?.focus();
  }, [validationError, inputRef]);

  const stateLoad =
    (state.loading || isLoading || evaluateGeolocation) ?? false;

  return (
    <div data-testid="zip-code-container">
      <TextFieldWrapper validationError={!!validationError}>
        <LocationInput
          handleTextChange={handleTextChange}
          hasValidationError={!!validationError}
          inputText={inputText}
          loading={stateLoad}
          onLocationSelect={onLocationSelect}
          predictions={predictions}
          textFieldLabel={t(TEXT.TextFieldLabel)}
          validationErrorText={
            <TransWrapper>
              <Trans
                components={{
                  LightUnderline: (
                    <ClickableText
                      label={inputText}
                      url={ADOTCOM_STORE_LOCATOR_URL}
                    />
                  ),
                  Text: (
                    <Text color={colors.errorDeep} size={1} weight="feather" />
                  ),
                }}
                i18nKey={TEXT.ErrorTextFieldHelper}
              />
            </TransWrapper>
          }
        />
      </TextFieldWrapper>
      <GeolocationButton {...props} />
    </div>
  );
};
